import { useEffect, useRef } from 'react';

import {
  failOperation,
  startOperation,
  succeedOperation,
} from './operations.js';

/**
 * Ends, on the page that calls it, a RUM operation that a button elsewhere
 * started before navigating there (e.g. "New plan" → the plan creation page).
 *
 * - `succeed` once the work is done.
 * - `fail` when it failed. Call `restartIfEnded` before every submit: after a
 *   failure it starts a new operation described as `retry`, so each attempt
 *   is measured on its own.
 * - Unmounting while the operation is open (any way of leaving the page)
 *   fails it as `abandoned`. A failure the user does not retry stays `error`.
 *
 * Opening the page without the start button (a direct link) has no start
 * event; Datadog drops the end events of such operations.
 *
 * Pass `name` as a literal: `@mint/datadog-operation-name` checks it like the
 * name of {@link startOperation}.
 */
export function usePageOperation(name: string) {
  const isOpenRef = useRef(true);

  useEffect(() => {
    isOpenRef.current = true;

    return () => {
      if (isOpenRef.current) {
        isOpenRef.current = false;
        failOperation(name, 'abandoned');
      }
    };
  }, [name]);

  const restartIfEnded = () => {
    if (!isOpenRef.current) {
      isOpenRef.current = true;
      startOperation(name, { description: 'retry' });
    }
  };

  const succeed = (context?: Record<string, unknown>) => {
    isOpenRef.current = false;
    succeedOperation(name, { context });
  };

  const fail = (context?: Record<string, unknown>) => {
    isOpenRef.current = false;
    failOperation(name, 'error', { context });
  };

  return { restartIfEnded, succeed, fail };
}
